import { useCallback, useEffect, useRef, useState } from "react";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Controlled-form auto-save (no react-hook-form). Tracks dirty fields against the
 * last-saved baseline, debounces, and PATCHes ONLY the changed fields — so a typing
 * session produces one coalesced save (and one audit row), not one per keystroke.
 * Flushes on unmount so nothing is lost on navigate-away (PRD §7.4).
 */
export function useAutoSave<T extends Record<string, unknown>>({
  initial,
  onSave,
  delay = 800,
}: {
  initial: T;
  onSave: (changed: Partial<T>) => Promise<void>;
  delay?: number;
}) {
  const [values, setValues] = useState<T>(initial);
  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const valuesRef = useRef<T>(values);
  valuesRef.current = values;
  const baselineRef = useRef<T>(initial);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computeChanged = useCallback((): Partial<T> => {
    const out: Partial<T> = {};
    for (const k of Object.keys(valuesRef.current) as (keyof T)[]) {
      if (JSON.stringify(valuesRef.current[k]) !== JSON.stringify(baselineRef.current[k])) {
        out[k] = valuesRef.current[k];
      }
    }
    return out;
  }, []);

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const changed = computeChanged();
    if (Object.keys(changed).length === 0) return;
    setStatus("saving");
    try {
      await onSaveRef.current(changed);
      baselineRef.current = { ...valuesRef.current };
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }, [computeChanged]);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setStatus("idle");
  }, []);

  // debounce a save whenever values diverge from the baseline. `values` is the
  // intentional re-run trigger; the reads themselves go through refs.
  // biome-ignore lint/correctness/useExhaustiveDependencies: values triggers the debounce
  useEffect(() => {
    if (Object.keys(computeChanged()).length === 0) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [values, delay, flush, computeChanged]);

  // flush pending edits on unmount (navigate-away safety)
  useEffect(() => () => void flush(), [flush]);

  return { values, setField, setValues, status, flush };
}
